import dayjs from '@utils/dateUtils';
import {
  availableLanguages,
  getLanguageTag,
  getLocaleDateTimeKey,
  setNumeralLocale,
} from '@utils/translations';
import { CookieStorage } from 'cookie-storage';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Language =
  | 'pt-BR'
  | 'pt-PT'
  | 'es-AR'
  | 'en-US'
  | 'en-GB'
  | 'es-ES'
  | 'it-IT'
  | 'de-DE'
  | 'hu-HU'
  | 'tr-TR';

export type AvailableLanguages = { [key: string]: string };

const SystemLanguageContext = createContext<
  [Language: Language, setLanguage: (lng: Language) => void, AvailableLanguages]
>(['en-US', () => {}, {}]);

export const useSystemLanguage = () => useContext(SystemLanguageContext);

const cookieStorage = new CookieStorage();

const COOKIE_CROPWISE_LANGUAGE = 'cropwise-language';

export const updateCropwiseElementsLang = (lng: string) => {
  cookieStorage.setItem(COOKIE_CROPWISE_LANGUAGE, lng, {
    path: '/',
    domain: globalThis.location.hostname?.includes('cropwise.com') ? 'cropwise.com' : '',
    expires: new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000), // avoid expire in near future (10 years)
  });
};

export const loadSystemLanguage = (): Language => {
  const storedLanguage = localStorage.getItem('language');

  if (storedLanguage && availableLanguages[storedLanguage]) {
    updateCropwiseElementsLang(availableLanguages[storedLanguage]);
    return storedLanguage as Language;
  } else {
    const newLanguage = 'en-US';
    updateCropwiseElementsLang(availableLanguages[newLanguage]);
    return newLanguage;
  }
};

export function SystemLanguageContextProvider({ children }: Readonly<React.PropsWithChildren>) {
  const { i18n } = useTranslation();
  const [stateLng, setStateLng] = useState<Language>(loadSystemLanguage());

  // Setup i18n, dayjs and numeraljs once, from the initial language resolved on mount.
  // Later changes flow through setSystemLanguage, so this intentionally runs mount-only.
  useEffect(() => {
    const languageTag = getLanguageTag(stateLng);
    i18n.changeLanguage(stateLng);
    dayjs.locale(getLocaleDateTimeKey(stateLng));
    setNumeralLocale(languageTag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSystemLanguage = useCallback(
    (lng: Language) => {
      localStorage.setItem('language', lng);
      i18n.changeLanguage(lng);
      const languageTag = getLanguageTag(lng);
      dayjs.locale(getLocaleDateTimeKey(lng));
      setStateLng(lng);
      setNumeralLocale(languageTag);
      // Keep the cropwise-elements cookie in sync so remote MFEs (e.g. the
      // subscriptions management module) pick up runtime language changes too.
      updateCropwiseElementsLang(availableLanguages[lng]);
      globalThis.engagement?.updateLanguage(lng);
    },
    [i18n]
  );

  const value = useMemo((): [
    Language: Language,
    setLanguage: (lng: Language) => void,
    AvailableLanguages
  ] => {
    return [stateLng, setSystemLanguage, availableLanguages];
  }, [stateLng, setSystemLanguage]);

  return <SystemLanguageContext.Provider value={value}>{children}</SystemLanguageContext.Provider>;
}
